import { useState } from "react";
import {
  CheckCircle2,
  XCircle,
  Plus,
  Pencil,
  Trash2,
  Power,
  Copy,
  Check,
} from "lucide-react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import api from "../../lib/api";

export interface UserRow {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  companies?: { id: string; company: { id: string; name: string } }[];
  _count: { ownedCompanies: number };
}

interface UsersTabProps {
  users: UserRow[];
  companies: { id: string; name: string }[];
  search: string;
  currentUserId: string;
  reload: () => Promise<void>;
}

const ROLES = [
  { value: "SUPERADMIN", label: "Superadmin" },
  { value: "OWNER", label: "Eigenaar" },
  { value: "ADMIN", label: "Beheerder" },
  { value: "USER", label: "Gebruiker" },
];

type ModalState =
  | { type: "create" }
  | { type: "edit"; user: UserRow }
  | { type: "delete"; user: UserRow }
  | null;

export function UsersTab({ users, companies, search, currentUserId, reload }: UsersTabProps) {
  const [modal, setModal] = useState<ModalState>(null);
  const [toast, setToast] = useState<string | null>(null);

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const filtered = users.filter(
    (u) =>
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const toggleStatus = async (user: UserRow) => {
    try {
      await api.patch(`/api/superadmin/users/${user.id}/status`, { isActive: !user.isActive });
      await reload();
      flash(`Gebruiker ${user.isActive ? "gedeactiveerd" : "geactiveerd"}.`);
    } catch (err: any) {
      flash(err?.response?.data?.error || "Actie mislukt");
    }
  };

  return (
    <div className="space-y-4">
      {toast && (
        <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-2.5 text-sm font-semibold">
          <CheckCircle2 size={24} /> {toast}
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={() => setModal({ type: "create" })}>
          <Plus size={27} /> Nieuwe gebruiker
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 border-b border-slate-100">
              <tr>
                <th className="text-left px-5 py-3 font-bold text-slate-600">Email</th>
                <th className="text-left px-5 py-3 font-bold text-slate-600">Naam</th>
                <th className="text-left px-5 py-3 font-bold text-slate-600">Rol</th>
                <th className="text-left px-5 py-3 font-bold text-slate-600">Bedrijven</th>
                <th className="text-left px-5 py-3 font-bold text-slate-600">Status</th>
                <th className="text-right px-5 py-3 font-bold text-slate-600">Acties</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors"
                >
                  <td className="px-5 py-3 font-medium text-slate-800">{user.email}</td>
                  <td className="px-5 py-3">
                    {user.firstName || user.lastName
                      ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                      : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <Badge status={user.role === "SUPERADMIN" ? "CREDIT" : "default"}>
                      {ROLES.find((r) => r.value === user.role)?.label || user.role}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-slate-600">
                    {user.companies?.length
                      ? user.companies.map((c) => c.company.name).join(", ")
                      : user._count.ownedCompanies}
                  </td>
                  <td className="px-5 py-3">
                    {user.isActive ? (
                      <span className="flex items-center gap-1 text-emerald-600 text-xs font-bold">
                        <CheckCircle2 size={21} /> Actief
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-600 text-xs font-bold">
                        <XCircle size={21} /> Inactief
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setModal({ type: "edit", user })}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-brand-600 transition-colors"
                        title="Bewerken"
                      >
                        <Pencil size={24} />
                      </button>
                      <button
                        onClick={() => toggleStatus(user)}
                        disabled={user.id === currentUserId}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-amber-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title={user.isActive ? "Deactiveren" : "Activeren"}
                      >
                        <Power size={24} />
                      </button>
                      <button
                        onClick={() => setModal({ type: "delete", user })}
                        disabled={user.id === currentUserId}
                        className="p-2 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Verwijderen"
                      >
                        <Trash2 size={24} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <p className="text-slate-500 text-center py-8">Geen gebruikers gevonden.</p>
        )}
      </Card>

      {modal?.type === "create" && (
        <UserFormModal
          companies={companies}
          onClose={() => setModal(null)}
          onSaved={async (msg) => {
            await reload();
            setModal(null);
            flash(msg);
          }}
        />
      )}
      {modal?.type === "edit" && (
        <UserFormModal
          user={modal.user}
          companies={companies}
          onClose={() => setModal(null)}
          onSaved={async (msg) => {
            await reload();
            setModal(null);
            flash(msg);
          }}
        />
      )}
      {modal?.type === "delete" && (
        <DeleteUserModal
          user={modal.user}
          onClose={() => setModal(null)}
          onDone={async (msg) => {
            await reload();
            setModal(null);
            flash(msg);
          }}
        />
      )}
    </div>
  );
}

interface UserFormModalProps {
  user?: UserRow;
  companies: { id: string; name: string }[];
  onClose: () => void;
  onSaved: (msg: string) => Promise<void> | void;
}

function UserFormModal({ user, companies, onClose, onSaved }: UserFormModalProps) {
  const isEdit = !!user;
  const [email, setEmail] = useState(user?.email || "");
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [role, setRole] = useState(user?.role || "USER");
  const [companyId, setCompanyId] = useState("");
  const [memberRole, setMemberRole] = useState("USER");
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!email.trim()) {
      setError("E-mail is verplicht");
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await api.patch(`/api/superadmin/users/${user!.id}`, {
          email,
          firstName,
          lastName,
          role,
        });
        await onSaved("Gebruiker opgeslagen.");
      } else {
        const { data } = await api.post("/api/superadmin/users", {
          email,
          firstName,
          lastName,
          role,
          ...(companyId ? { companyId, memberRole } : {}),
        });
        if (data.generatedPassword) {
          setGeneratedPassword(data.generatedPassword);
          return;
        }
        await onSaved("Gebruiker aangemaakt.");
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Opslaan mislukt");
    } finally {
      setLoading(false);
    }
  };

  if (generatedPassword) {
    return (
      <PasswordRevealModal
        password={generatedPassword}
        title="Gebruiker aangemaakt"
        email={email}
        onClose={() => onSaved("Gebruiker aangemaakt.")}
      />
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? "Gebruiker bewerken" : "Nieuwe gebruiker"}
      size="md"
      footer={
        <div className="flex justify-between items-center">
          {error ? <p className="text-sm text-red-600">{error}</p> : <span />}
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose} disabled={loading}>
              Annuleren
            </Button>
            <Button variant="primary" onClick={submit} loading={loading}>
              {isEdit ? "Opslaan" : "Aanmaken"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <Input label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Voornaam" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <Input label="Achternaam" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <div>
          <Select
            label="Rol (platform-breed)"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            options={ROLES}
          />
          <p className="text-xs text-slate-400 mt-1">
            Bepaalt wat dit account op het platform mag. <strong>Superadmin</strong> beheert alle
            bedrijven; <strong>Gebruiker</strong> is de standaard voor een medewerker die aan één
            bedrijf wordt gekoppeld.
          </p>
        </div>
        {!isEdit && (
          <>
            <div className="pt-2 border-t border-slate-100 space-y-3">
              <Select
                label="Koppel aan bedrijf (optioneel)"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                options={[
                  { value: "", label: "— Geen koppeling —" },
                  ...companies.map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
              {companyId && (
                <div>
                  <Select
                    label="Rol binnen dit bedrijf"
                    value={memberRole}
                    onChange={(e) => setMemberRole(e.target.value)}
                    options={[
                      { value: "USER", label: "Gebruiker" },
                      { value: "ADMIN", label: "Beheerder" },
                    ]}
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Rechten binnen dit bedrijf. <strong>Beheerder</strong> mag het bedrijf beheren,
                    <strong> Gebruiker</strong> alleen werken in de boekhouding. Het eigenaarschap
                    zelf regel je apart bij het bedrijf (tab Eigenaar wijzigen).
                  </p>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-400">
              Er wordt automatisch een tijdelijk wachtwoord gegenereerd dat je na het aanmaken eenmalig
              kunt kopiëren.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}

function DeleteUserModal({
  user,
  onClose,
  onDone,
}: {
  user: UserRow;
  onClose: () => void;
  onDone: (msg: string) => Promise<void> | void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [counts, setCounts] = useState<any>(null);

  const tryDelete = async () => {
    setLoading(true);
    setError("");
    try {
      await api.delete(`/api/superadmin/users/${user.id}`);
      await onDone("Gebruiker verwijderd.");
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setCounts(err.response.data.counts);
        setError(err.response.data.error);
      } else {
        setError(err?.response?.data?.error || "Verwijderen mislukt");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Gebruiker verwijderen"
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Annuleren
          </Button>
          <Button variant="danger" onClick={tryDelete} loading={loading}>
            Verwijderen
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Weet je zeker dat je <strong>{user.email}</strong> wilt verwijderen?
        </p>
        {error && (
          <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm">
            {error}
          </div>
        )}
        {counts && (
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm space-y-1">
            <p className="font-bold">Gekoppelde data:</p>
            <ul className="list-disc list-inside">
              {counts.ownedCompanies > 0 && <li>{counts.ownedCompanies} bedrijf(s) als eigenaar</li>}
              {counts.createdInvoices > 0 && <li>{counts.createdInvoices} factuur(en)</li>}
              {counts.createdQuotes > 0 && <li>{counts.createdQuotes} offerte(s)</li>}
              {counts.createdPayments > 0 && <li>{counts.createdPayments} betaling(en)</li>}
              {counts.createdExpenses > 0 && <li>{counts.createdExpenses} uitgave(n)</li>}
            </ul>
            <p className="mt-2 font-semibold">Deactiveer de gebruiker in plaats van verwijderen.</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

function PasswordRevealModal({
  password,
  email,
  title,
  onClose,
}: {
  password: string;
  email: string;
  title: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <Button variant="primary" className="w-full" onClick={onClose}>
          Gereed
        </Button>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Tijdelijk wachtwoord voor <strong>{email}</strong>:
        </p>
        <div className="flex items-center gap-2">
          <div className="flex-1 p-3 rounded-xl bg-slate-50 border border-slate-200 font-mono text-lg font-bold text-slate-800 break-all">
            {password}
          </div>
          <Button variant="outline" size="sm" onClick={copy}>
            {copied ? <Check size={24} /> : <Copy size={24} />}
          </Button>
        </div>
        <p className="text-xs text-slate-400">
          Dit wachtwoord is alleen nu zichtbaar. Deel het veilig met de gebruiker.
        </p>
      </div>
    </Modal>
  );
}